import useRequest from "@/lib/request/useRequest";
import { faClose, faUpload } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Button, Select, SelectItem, Selection } from "@heroui/react";
import { useCallback, useMemo, useRef, useState } from "react";
import useFieldDashboard from "@/lib/fieldDashboard/useFieldDashboard";
import { useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";

const NON_DATA_LAYER_TYPES = [
    { key: "field_boundary", label: "Field Boundary" },
    { key: "irrigated_boundary", label: "Irrigated Boundary" },
    { key: "management_zones", label: "Management Zones" },
    { key: "ab_line", label: "AB Line" },
    { key: "plots", label: "Plots" },
    { key: "treatment_sectors", label: "Treatment Sectors" },
    { key: "imagery", label: "Imagery" },
    { key: "soil sample", label: "Soil Sample" },
    { key: "soil map", label: "Soil Map" },
    { key: "topography", label: "Topography" },
    { key: "other", label: "Other" },
];

const DATA_LAYER_TYPES = [
    { key: "harvest", label: "Harvest" },
    { key: "application", label: "Application" },
    { key: "tillage", label: "Tillage" },
    { key: "planting", label: "Planting" },
    { key: "imagery", label: "Imagery" },
    { key: "soil sample", label: "Soil Sample" },
    { key: "soil map", label: "Soil Map" },
    { key: "topography", label: "Topography" },
    { key: "other", label: "Other" },
];

function UploadDataLayer({
    dataLayerOnly = false,
}: {
    dataLayerOnly?: boolean;
}) {
    const { t } = useTranslation();
    const { fieldId, fieldQuery } = useFieldDashboard() ?? {};
    const queryClient = useQueryClient();
    const { useMutateRequest } = useRequest();

    const inputRef = useRef<HTMLInputElement>(null);

    const [selectedFile, setSelectedFile] = useState<File | null>(null);
    const [uploadType, setUploadType] = useState<Selection>(new Set());

    const uploadDataLayer = useMutateRequest({
        successMessage: t("success.data_layer_uploaded_successfully", {
            ns: "messages",
        }),
        errorMessage: t("error.failed_to_upload_data_layer", {
            ns: "messages",
        }),
    });

    const onSuccess = useMemo(() => {
        return () => {
            fieldQuery?.refetch();
            queryClient.invalidateQueries({
                queryKey: [`fields/${fieldId}/data_layers/`],
                refetchType: "active",
                exact: true,
            });
            queryClient.invalidateQueries({
                queryKey: [`fields/${fieldId}/`],
                refetchType: "active",
                exact: true,
            });
            setSelectedFile(null);
            setUploadType(new Set());
            if (inputRef.current) {
                inputRef.current.value = "";
            }
        };
    }, [fieldId, fieldQuery, queryClient]);

    const uploadFieldBoundary = useCallback(async () => {
        if (
            !selectedFile ||
            !fieldQuery?.data?.name ||
            !fieldQuery?.data?.grower
        )
            return;
        const formData = new FormData();
        formData.append("file", selectedFile);
        formData.append("name", fieldQuery?.data?.name ?? "");
        formData.append("grower", fieldQuery?.data?.grower?.toString() ?? "");
        uploadDataLayer.mutate(
            {
                url: `fields/${fieldId}/upload/`,
                method: "put",
                data: formData,
            },
            {
                onSuccess,
            },
        );
    }, [
        fieldId,
        fieldQuery?.data?.grower,
        fieldQuery?.data?.name,
        selectedFile,
        uploadDataLayer,
        onSuccess,
    ]);

    const uploadIrrigatedBoundary = useCallback(async () => {
        if (!selectedFile || !fieldQuery?.data?.irrigation_systems[0]?.id)
            return;
        const formData = new FormData();
        formData.append("file", selectedFile);
        uploadDataLayer.mutate(
            {
                url: `irrigation_systems/${fieldQuery?.data?.irrigation_systems[0]?.id}/upload_boundary/`,
                method: "put",
                data: formData,
            },
            {
                onSuccess,
            },
        );
    }, [
        fieldQuery?.data?.irrigation_systems,
        onSuccess,
        selectedFile,
        uploadDataLayer,
    ]);

    const uploadSectors = useCallback(async () => {
        if (!selectedFile) return;
        const formData = new FormData();
        formData.append("file", selectedFile);
        uploadDataLayer.mutate(
            {
                url: `irrigation_systems/${fieldQuery?.data?.irrigation_systems[0]?.id}/upload_sectors/`,
                method: "put",
                data: formData,
            },
            {
                onSuccess,
            },
        );
    }, [
        fieldQuery?.data?.irrigation_systems,
        onSuccess,
        selectedFile,
        uploadDataLayer,
    ]);

    const uploadManagementZones = useCallback(async () => {
        if (!selectedFile) return;
        const formData = new FormData();
        formData.append("file", selectedFile);
        uploadDataLayer.mutate(
            {
                url: `fields/${fieldId}/management_zones/`,
                method: "post",
                data: formData,
            },
            {
                onSuccess,
            },
        );
    }, [fieldId, onSuccess, selectedFile, uploadDataLayer]);

    const uploadAbLine = useCallback(async () => {
        if (!selectedFile) return;
        const formData = new FormData();
        formData.append("file", selectedFile);
        uploadDataLayer.mutate(
            {
                url: `/ab_lines/upload/`,
                method: "post",
                data: formData,
            },
            {
                onSuccess,
            },
        );
    }, [onSuccess, selectedFile, uploadDataLayer]);

    const uploadPlots = useCallback(async () => {
        if (!selectedFile) return;
        const formData = new FormData();
        formData.append("file", selectedFile);
        uploadDataLayer.mutate(
            {
                url: `fields/${fieldId}/plots/`,
                method: "post",
                data: formData,
            },
            {
                onSuccess,
            },
        );
    }, [fieldId, onSuccess, selectedFile, uploadDataLayer]);

    const uploadTrueDataLayer = useCallback(async () => {
        if (
            !selectedFile ||
            !uploadType ||
            uploadType == "all" ||
            uploadType.size == 0
        )
            return;
        const formData = new FormData();
        formData.append("file", selectedFile);
        formData.append("name", selectedFile.name);
        formData.append(
            "layer_type",
            ([...uploadType][0] as string) || "other",
        );
        uploadDataLayer.mutate(
            {
                url: `fields/${fieldId}/data_layers/`,
                method: "post",
                data: formData,
            },
            {
                onSuccess,
            },
        );
    }, [selectedFile, uploadType, uploadDataLayer, fieldId, onSuccess]);

    const onUpload = useCallback(() => {
        if (uploadType == "all" || uploadType.size == 0) return;
        if (uploadType.has("field_boundary")) {
            uploadFieldBoundary();
        } else if (uploadType.has("irrigated_boundary")) {
            uploadIrrigatedBoundary();
        } else if (uploadType.has("management_zones")) {
            uploadManagementZones();
        } else if (uploadType.has("ab_line")) {
            uploadAbLine();
        } else if (uploadType.has("plots")) {
            uploadPlots();
        } else if (uploadType.has("treatment_sectors")) {
            uploadSectors();
        } else {
            uploadTrueDataLayer();
        }
    }, [
        uploadType,
        uploadFieldBoundary,
        uploadIrrigatedBoundary,
        uploadManagementZones,
        uploadAbLine,
        uploadPlots,
        uploadSectors,
        uploadTrueDataLayer,
    ]);

    return (
        <div className="flex flex-row gap-2 items-end">
            <div className="flex flex-row gap-2 items-center">
                <Button
                    startContent={<FontAwesomeIcon icon={faUpload} />}
                    size="sm"
                    color="primary"
                    endContent={
                        <input
                            type="file"
                            hidden
                            ref={inputRef}
                            onChange={(e) => {
                                if (e.target.files?.[0]) {
                                    setSelectedFile(e.target.files[0]);
                                }
                            }}
                        />
                    }
                    onPress={() => {
                        inputRef.current?.click();
                    }}
                    isLoading={uploadDataLayer.isPending}
                >
                    {selectedFile
                        ? selectedFile.name
                        : t("management_zones.upload_data_layer", {
                              ns: "setup",
                          })}
                </Button>

                {selectedFile && (
                    <Button
                        color="danger"
                        size="sm"
                        onPress={() => {
                            setSelectedFile(null);
                        }}
                        isIconOnly
                        aria-label="Remove File"
                        isDisabled={uploadType == "all" || uploadType.size == 0}
                        isLoading={uploadDataLayer.isPending}
                    >
                        <FontAwesomeIcon icon={faClose} />
                    </Button>
                )}
            </div>
            {selectedFile && (
                <>
                    <Select
                        selectedKeys={uploadType}
                        onSelectionChange={setUploadType}
                        placeholder={t("management_zones.data_type", {
                            ns: "setup",
                        })}
                        size="sm"
                        label={t("management_zones.upload_type", {
                            ns: "setup",
                        })}
                        labelPlacement="outside"
                        showScrollIndicators={false}
                    >
                        {(dataLayerOnly
                            ? DATA_LAYER_TYPES
                            : NON_DATA_LAYER_TYPES
                        ).map((type) => (
                            <SelectItem key={type.key}>{type.label}</SelectItem>
                        ))}
                    </Select>
                    <Button
                        size="sm"
                        color="primary"
                        onPress={onUpload}
                        isDisabled={uploadType == "all" || uploadType.size == 0}
                        isLoading={uploadDataLayer.isPending}
                    >
                        {t("management_zones.upload", {
                            ns: "setup",
                        })}
                    </Button>
                </>
            )}
        </div>
    );
}
export default UploadDataLayer;
